import React, {
  useCallback,
  useState,
  useRef,
  useEffect,
  useContext,
} from "react";
import { useForm } from "react-hook-form";
import { Input } from "rizzui/input";
import { Radio } from "rizzui/radio";
import { Text } from "rizzui/typography";
import cn from "../lib/utils/class-name";
import { Button } from "rizzui/button";
import { useDropzone } from "react-dropzone";
import MicrophoneIcon from "../assets/mic";
import { spanStyle, textStyle } from "./cards/importFile";
import { ActionIcon } from "rizzui/action-icon";
import BinIcon from "../assets/bin";
import { Progressbar } from "rizzui/progressbar";
import { Box } from "rizzui/box";
import { useModal } from "../hooks/useModal";
import RecordPlugin from "wavesurfer.js/dist/plugins/record.esm.js";
import WaveSurfer from "wavesurfer.js";
import CancelIcon from "../assets/cancel";
import DoneIcon from "../assets/done";
import { HiOutlinePlayCircle } from "react-icons/hi2";
import { inputClassName, labelClassName } from "../modules/projects/new/first";
import axios from "axios";
import { baseUrl } from "../modules/account/login";
import { AuthContext } from "../context/authContext";
import { useNotifcication } from "../hooks/useNotification";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router";

export default function ImportVoice() {
  const {
    register,
    formState: { errors },
    handleSubmit,
    reset,
    getValues,
  } = useForm();
  const { openNotification } = useNotifcication();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { token } = useContext(AuthContext);
  const AuthStr = "Bearer ".concat(token!);

  const handleVoiceImport = () => {
    const { voice_name, voice_gender, tone } = getValues();
    const voiceFile = addedFile
      ? addedFile
      : new File([blob!], "voicetesting.mp3", { type: "audio/mpeg" });

    const formData = new FormData();
    formData.append("voice_file", voiceFile);
    formData.append("voice_name", voice_name);
    formData.append("tone", tone);
    formData.append("gender", voice_gender);
    return axios
      .post(`${baseUrl}/voices`, formData, {
        headers: { Authorization: AuthStr },
      })
      .then((response) => {
        if (response) {
          queryClient.invalidateQueries({ queryKey: ["getAgents", token] });
          openNotification({
            status: "success",
            message: "Voice Cloned",
          });
          closeModal();
        }
        return response;
      })
      .catch((e) => {
        if (e?.response?.data?.message === "Session expired")
          navigate("/account/logout");
        return Promise.reject(
          new Error(e?.response?.data?.message ?? "Something went wrong")
        );
      });
  };

  const {
    data: uploadData,
    error: uploadError,
    isLoading: uploadLoading,
    refetch,
  } = useQuery({
    queryKey: ["new_agent", token],
    queryFn: () => handleVoiceImport(),
    enabled: false,
    retry: 0,
  });

  const { closeModal } = useModal();
  const handleImport = handleSubmit((data) => {
    if (blob || addedFile) {
      refetch();
    } else {
      setRecError("Voice recording is required");
    }
  });

  const [blob, setBlob] = useState<Blob>();
  const waveformRef = useRef<any>(null);
  const [recording, setRecording] = useState(false);
  const [error, setError] = useState("");
  const [recError, setRecError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isDone, setIsDone] = useState(false);
  const [fileName, setFileName] = useState("");
  const [progress, setProgress] = useState<any>("0");
  const [disabled, setDisabled] = useState(false);
  const [recordInit, setRecordInit] = useState<RecordPlugin | null>(null);
  const [recordWave, setRecordWave] = useState<WaveSurfer | undefined>();
  const [addedFile, setAddedFile] = useState<File>();
  const onDrop = useCallback(
    (acceptedFiles) => {
      setError("");
      const ext = acceptedFiles[0].name.split(".").pop().toLowerCase();
      if (acceptedFiles.length > 1) {
        setError("You can only upload one file at a time");
      } else if (ext !== "mp3") setError("Upload a MP3 file");
      else {
        setIsLoading(true);
        setFileName(acceptedFiles[0].name);
        const reader = new FileReader();
        reader.readAsDataURL(acceptedFiles[0]);
        reader.onprogress = (e) => {
          setProgress(Math.ceil((e.loaded / e.total) * 100));
        };
        reader.onload = () => {
          setAddedFile(acceptedFiles[0]);
          setIsDone(true);
        };
      }
    },
    [addedFile]
  );
  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });
  const { view } = useModal();

  const handleDelete = () => {
    setIsLoading(false);
    setIsDone(false);
    setFileName("");
  };

  let wavesurfer, record;
  let scrollingWaveform = false;
  let continuousWaveform = true;
  const createWaveSurfer = () => {
    // Destroy the previous wavesurfer instance
    if (wavesurfer) {
      wavesurfer.destroy();
    }

    // Create a new Wavesurfer instance
    wavesurfer = WaveSurfer.create({
      container: "#waveform",
      height: 22,
      waveColor: "#F3F8FF",
      progressColor: "#758599",
      barGap: 2,
      barWidth: 2,
      hideScrollbar: true,
    });

    // Initialize the Record plugin
    record = wavesurfer.registerPlugin(
      RecordPlugin.create({
        renderRecordedAudio: true,
        scrollingWaveform,
        continuousWaveform,
        continuousWaveformDuration: 60, // optional
      })
    );
    setRecordInit(record);
    setRecordWave(wavesurfer);

    // Render recorded audio
    record.on("record-end", (blob) => {
      //const recordedUrl = URL.createObjectURL(blob);
      setBlob(blob);
    });

    record.on("destroy", () => {
      setProgress("00:00");
      setDisabled(false);
      setBlob(undefined);
      console.log(blob);
      wavesurfer.destroy();
    });

    record.on("record-progress", (time) => {
      if (time > 60001) {
        record.stopRecording();
      } else updateProgress(time);
    });
  };

  const handleRecord = () => {
    setProgress("00:00");
    createWaveSurfer();
    if (record.isRecording() || record.isPaused()) {
      record.stopRecording();
      return;
    }
    setDisabled(true);
    record.startRecording();
  };

  const handleCancel = () => {
    if (progress !== "00:00" && progress !== "0") {
      recordInit?.stopRecording();
      recordWave?.destroy();
      setProgress("00:00");
      setDisabled(false);
      setBlob(undefined);
    } else {
      setRecording(false);
    }
  };
  const handleDone = () => {
    recordInit?.stopRecording();
    setIsDone(true);
  };

  const handleDeleteRecord = () => {
    setProgress("00:00");
    setDisabled(false);
    setBlob(undefined);
    recordInit?.stopRecording();
    setRecError("");
    recordWave?.destroy();
    setIsDone(false);
  };
  const updateProgress = (time) => {
    // time will be in milliseconds, convert it to mm:ss format
    setRecError("");
    const formattedTime = [
      Math.floor((time % 3600000) / 60000), // minutes
      Math.floor((time % 60000) / 1000), // seconds
    ]
      .map((v) => (v < 10 ? "0" + v : v))
      .join(":");
    setProgress(formattedTime);
  };

  useEffect(() => {
    handleDeleteRecord();
    reset();
    setError("");
    setAddedFile(undefined);
    handleDelete();
    queryClient.resetQueries({ queryKey: ["new_agent", token] });
  }, [view]);

  return (
    <div className="md:w-[471px] w-[96%] mx-auto min-h-[574px] max-h-[100%] overflow-y-auto bg-background dark:bg-[#2A2C30] p-[32px] rounded-[8px] border-[0.5px] border-muted">
      <button
        className="absolute top-4 right-4 p-1 rounded-lg z-10 text-gray-400 bg-transparent hover:text-gray-600 dark:hover:text-gray-300"
        onClick={closeModal}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="currentColor"
          className="size-[18px]"
        >
          <path
            fillRule="evenodd"
            d="M5.47 5.47a.75.75 0 0 1 1.06 0L12 10.94l5.47-5.47a.75.75 0 1 1 1.06 1.06L13.06 12l5.47 5.47a.75.75 0 1 1-1.06 1.06L12 13.06l-5.47 5.47a.75.75 0 0 1-1.06-1.06L10.94 12 5.47 6.53a.75.75 0 0 1 0-1.06Z"
            clipRule="evenodd"
          />
        </svg>
      </button>
      <form
        className="flex flex-col gap-[28px]"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          handleImport();
        }}
      >
        <div className="flex flex-col gap-[8px]">
          <Text className="font-ibmPlexSerif font-[600] text-primary-dark text-[18px] leading-[23.4px]">
            Import your voice
          </Text>
          <Text className="font-switzer font-[400] text-[#758599] text-[14px] leading-[18.48px]">
            Record your voice or upload your with a duration of less than 1
            minute
          </Text>
        </div>

        <Input
          label="Name"
          type="text"
          inputClassName={inputClassName}
          labelClassName={labelClassName}
          {...register("voice_name", { required: true })}
          error={errors?.name ? "Required" : ""}
        />

        <div className="flex flex-col gap-[16px]">
          <Text className={labelClassName}>Gender</Text>
          <div className="flex gap-[24px]">
            <Radio
              label="Male"
              value="male"
              size="sm"
              labelClassName={cn(
                labelClassName,
                "!text-primary-dark ml-[16px]"
              )}
              {...register("voice_gender", {
                required: true,
              })}
            />

            <Radio
              label="Female"
              size="sm"
              value="female"
              labelClassName={cn(
                labelClassName,
                "!text-primary-dark ml-[16px]"
              )}
              {...register("voice_gender", {
                required: "Required",
              })}
            />
          </div>

          {errors.voice_gender?.message && (
            <p className="text-red text-[13px]">{"Select an option"}</p>
          )}
        </div>

        <Input
          label="Tone"
          type="text"
          inputClassName={inputClassName}
          labelClassName={labelClassName}
          placeholder="E.g,friendly, professional, warm"
          {...register("tone", { required: "Required" })}
          error={errors?.tone?.message ? "" + errors?.tone?.message : ""}
        />

        {recording && !isDone && (
          <>
            <div className="!h-[42px] grid grid-cols-11 bg-[#3B434D] rounded-[50px] content-center pl-[16px]">
              <div className="col-span-3 flex flex-row items-center">
                <ActionIcon
                  className="!bg-transparent rounded-full !border-none"
                  disabled={disabled}
                  onClick={handleRecord}
                >
                  <MicrophoneIcon className={"text-[#C3DEFF]"} />
                </ActionIcon>
                <Text className="font-switzer font-[400] text-[12px] leading-[15.84px] text-[#758599]">
                  {progress}/01:00
                </Text>
              </div>
              <div
                id="waveform"
                ref={waveformRef}
                className={cn("col-span-5 content-center")}
              ></div>

              <div className="col-span-3 h-fit flex flex-row justify-center">
                <ActionIcon
                  className="!bg-transparent rounded-full"
                  onClick={handleCancel}
                >
                  <CancelIcon />
                </ActionIcon>
                <ActionIcon
                  className="!bg-transparent rounded-full !border-none"
                  disabled={progress === "00:00" ? true : false}
                  onClick={handleDone}
                >
                  <DoneIcon />
                </ActionIcon>
              </div>
            </div>
            <Text className="text-red font-switzer text-[12px] mt-[-24px]">
              {recError}
            </Text>
          </>
        )}
        {recording && isDone && (
          <div className="w-full border border-dashed border-[#758599] h-[52px] flex flex-row justify-between items-center rounded-[4px] px-[16px]">
            <div className="flex flex-row gap-[8px] text-[#758599] items-center">
              <MicrophoneIcon className={"text-[#758599]"} />
              <Text className="text-[12px] font-[400] font-switzer leading-[15.84px]">
                voicetesting.mp3
              </Text>
            </div>
            <ActionIcon
              className="!bg-transparent !p-0 w-fit"
              onClick={handleDeleteRecord}
            >
              <BinIcon className={""} />
            </ActionIcon>
          </div>
        )}
        {!recording && (
          <>
            <div
              className={cn(
                "border border-[#758599] border-dashed w-full rounded-[8px] flex place-content-center place-items-center justify-center",
                isLoading ? "h-[79px] py-[16px]" : "",
                isDone ? "h-[52px] py-[16px]" : "",
                !isDone && !isLoading ? "h-[102px]" : "",
                isDragActive ? "dark:bg-[#FFFFFF]/10 bg-[#000]/10" : "",
                error ? "border-red" : ""
              )}
            >
              <div className="w-full flex flex-row justify-center">
                {!isLoading ? (
                  <div
                    {...getRootProps({
                      onClick: () => {
                        setError("");
                      },
                    })}
                    className="min-w-[172px] place-content-center place-items-center text-center"
                  >
                    <div className="flex justify-center">
                      <MicrophoneIcon className={"text-[#758599]"} />
                    </div>
                    <Text className={cn(textStyle, "mt-[10px]")}>
                      Drag and drop your audio here <br />
                      or click{" "}
                      <span className={cn(spanStyle, "cursor-pointer")}>
                        Browse
                      </span>{" "}
                      to select an audio file
                    </Text>
                    <input
                      {...getInputProps()}
                      type="file"
                      multiple={false}
                      accept=".mp3"
                    />
                  </div>
                ) : (
                  <div className="grid grid-cols-8 gap-[4px] w-[90%]">
                    <div className="col-span-1 place-content-center">
                      <MicrophoneIcon className={""} />
                    </div>
                    <div className="col-span-7 items-center flex">
                      <div className="w-full grid grid-cols-1 place-content-center">
                        <Text
                          className={cn(
                            textStyle,
                            "text-[12px] leading-[0px]",
                            isDone ? "mb-0" : "mb-[12px]"
                          )}
                        >
                          {fileName}
                        </Text>
                        {!isDone && (
                          <Progressbar
                            size="sm"
                            value={progress}
                            trackClassName="dark:bg-btn"
                          />
                        )}
                      </div>

                      <div className="place-items-end">
                        <ActionIcon
                          variant="text"
                          onClick={handleDelete}
                          className="size-[17px] !p-[0px]"
                        >
                          <BinIcon className="inline-block" />
                        </ActionIcon>

                        {!isDone && (
                          <Text className={cn(textStyle, "text-[12px]")}>
                            {progress}%
                          </Text>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            {error && (
              <Text className="-mt-[24px] font-[400] text-[12px] font-switzer text-red">
                {error}
              </Text>
            )}
          </>
        )}

        {!recording && !isDone && !isLoading && (
          <div className="flex flex-row gap-[21px]">
            <Box className="h-[1px] self-center bg-[#D1D6DD] flex-1" />
            <Text className="text-[12px] font-[400] text-[#758599]">OR</Text>
            <Box className="h-[1px] self-center bg-[#D1D6DD] flex-1" />
          </div>
        )}

        {recording ? (
          <Button
            className="h-[40px] bg-btn hover:!bg-btn font-[500] font-switzer text-[12px] leading-[15.84px] text-background"
            type="submit"
            isLoading={uploadLoading}
          >
            Clone your voice
          </Button>
        ) : (
          <div className="w-full flex flex-row justify-center">
            {!isDone && (
              <Button
                className="h-[40px] w-[183px] bg-btn hover:!bg-btn font-[500] font-switzer text-[12px] leading-[15.84px] text-background gap-[7px]"
                onClick={() => {
                  setAddedFile(undefined);
                  setRecording(true);
                }}
              >
                <MicrophoneIcon className={""} /> Start recording
              </Button>
            )}

            {isDone && (
              <Button
                className="h-[40px] w-[183px] bg-btn hover:!bg-btn font-[500] font-switzer text-[12px] leading-[15.84px] text-background gap-[7px]"
                type="submit"
                isLoading={uploadLoading}
              >
                Clone your voice
              </Button>
            )}
          </div>
        )}
      </form>
      {uploadError && (
        <Text className="font-[400] font-switzer text-[12px] text-red">
          {uploadError.message}
        </Text>
      )}
    </div>
  );
}
